import React, { useState } from "react";
import { Flex, Grid, Popconfirm, Tooltip, Typography, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { IoLogoWhatsapp } from "react-icons/io";
import { MdOutlineSaveAlt } from "react-icons/md";

import { copyText, waDigits, waLink } from "utils/clipboard";

const { Text } = Typography;

/**
 * A phone number in a list, with the three things people actually do with one:
 * copy it, keep it as the record's WhatsApp number, and open WhatsApp on it.
 *
 * The actions sit in the cell rather than in the row's Actions column because
 * they belong to this number - a row can carry both a phone and a separate
 * WhatsApp number, and an icon three columns away says nothing about which one
 * it would act on.
 *
 * `onSaveWhatsapp` takes the number and must resolve once the record the row
 * came from carries it; pass nothing to leave that icon out (the WhatsApp
 * number column itself has nowhere to save to).
 */
const PhoneNumberCell = ({
  value,
  waNumber,
  onSaveWhatsapp,
  // Shown in the confirmation so it is clear which record is being changed.
  recordLabel,
}) => {
  const [saving, setSaving] = useState(false);
  // On phones the number itself is a tel: link, so a tap goes straight to the dialer.
  const isMobile = !Grid.useBreakpoint().md;

  if (!value) return null;

  const telHref = `tel:${String(value).replace(/[^\d+]/g, "")}`;

  const dialable = waDigits(value);
  const alreadySaved = dialable && waDigits(waNumber) === dialable;

  const handleCopy = async () => {
    const copied = await copyText(value);
    message[copied ? "success" : "error"](
      copied ? "Phone number copied" : "Could not copy the number"
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveWhatsapp(value);
      message.success("Saved as the WhatsApp number");
    } catch (err) {
      message.error("Could not save the WhatsApp number");
    }
    setSaving(false);
  };

  const openWhatsapp = () => {
    const link = waLink(value);
    if (!link) {
      message.error("This number has no digits to dial");
      return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const saveTitle = alreadySaved
    ? "Already the WhatsApp number"
    : waNumber
      ? `Replace the WhatsApp number ${waNumber} with ${value}?`
      : `Save ${value} as the WhatsApp number?`;

  return (
    <Flex align="center" gap={8} style={{ minWidth: 0 }}>
      <Text ellipsis={{ tooltip: value }} style={{ flex: "1 1 auto", minWidth: 0 }}>
        {isMobile ? <a href={telHref}>{value}</a> : value}
      </Text>
      <Flex align="center" gap={8} style={{ flex: "0 0 auto" }}>
        <Tooltip title="Copy number">
          <a onClick={handleCopy} aria-label="Copy number">
            <CopyOutlined style={{ verticalAlign: "middle" }} />
          </a>
        </Tooltip>
        {onSaveWhatsapp && (
          <Popconfirm
            title="Save to WhatsApp field"
            description={
              recordLabel ? `${saveTitle} (${recordLabel})` : saveTitle
            }
            okText="Save"
            onConfirm={handleSave}
            disabled={alreadySaved || saving}
          >
            <Tooltip title={alreadySaved ? "Already the WhatsApp number" : "Save as WhatsApp number"}>
              <a
                aria-label="Save as WhatsApp number"
                style={{
                  color: alreadySaved ? "#bfbfbf" : "#00B96B",
                  cursor: alreadySaved ? "default" : "pointer",
                  lineHeight: 1,
                }}
              >
                <MdOutlineSaveAlt size="15" style={{ verticalAlign: "middle" }} />
              </a>
            </Tooltip>
          </Popconfirm>
        )}
        <Popconfirm
          title="Open WhatsApp"
          description={`Open a WhatsApp chat with ${value} in a new tab?`}
          okText="Open"
          onConfirm={openWhatsapp}
        >
          <Tooltip title="Open in WhatsApp">
            <a aria-label="Open in WhatsApp" style={{ lineHeight: 1 }}>
              <IoLogoWhatsapp color="#00B96B" size="15" style={{ verticalAlign: "middle" }} />
            </a>
          </Tooltip>
        </Popconfirm>
      </Flex>
    </Flex>
  );
};

export default PhoneNumberCell;
